import { createLogger } from "@/lib/logger";
import { useCallback, useEffect, useRef, useState } from "react";

const logger = createLogger("tutor-turn-translations");

export interface TutorTranscriptSegment {
  id?: string;
  role?: string;
  text?: string;
  isFinal?: boolean;
  final?: boolean;
  firstReceivedTime?: number;
}

export interface TutorTurnTranslationState {
  status: "loading" | "success" | "error";
  translation?: string;
  translationLanguage: string;
  error?: string;
}

interface UseTutorTurnTranslationsOptions {
  segments: TutorTranscriptSegment[];
  enabled: boolean;
  sourceLanguageCode: string;
  translationLanguageCode: string;
  translationLanguage: string;
}

function resolveFinality(segment: TutorTranscriptSegment): boolean | undefined {
  if (typeof segment.isFinal === "boolean") return segment.isFinal;
  if (typeof segment.final === "boolean") return segment.final;
  return undefined;
}

export function hasFinalizedTutorTurn(segments: TutorTranscriptSegment[]): boolean {
  return segments.some(
    (segment) =>
      segment.role === "assistant" &&
      typeof segment.text === "string" &&
      segment.text.trim().length > 0 &&
      resolveFinality(segment) === true
  );
}

function hashText(text: string): string {
  let hash = 5381;
  for (const character of text) {
    hash = (hash * 33) ^ character.codePointAt(0)!;
  }
  return (hash >>> 0).toString(36);
}

export function getTutorTurnKey(segment: TutorTranscriptSegment): string {
  const text = String(segment.text || "").trim();
  const identity = String(segment.id || segment.firstReceivedTime || "assistant-turn");
  return `${identity}:${hashText(text)}`;
}

export function getTutorTurnTranslationCacheKey(
  segment: TutorTranscriptSegment,
  translationLanguageCode: string
): string {
  return `${getTutorTurnKey(segment)}:${translationLanguageCode}`;
}

export function useTutorTurnTranslations({
  segments,
  enabled,
  sourceLanguageCode,
  translationLanguageCode,
  translationLanguage,
}: UseTutorTurnTranslationsOptions) {
  const [translations, setTranslations] = useState<Record<string, TutorTurnTranslationState>>({});
  const translationsRef = useRef(translations);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const observedByLanguageRef = useRef<Map<string, Set<string>>>(new Map());
  const previousConfigRef = useRef({ enabled: false, translationLanguageCode });

  const finalAssistantSegments = segments.filter(
    (segment) =>
      segment.role === "assistant" &&
      typeof segment.text === "string" &&
      segment.text.trim().length > 0 &&
      resolveFinality(segment) === true
  );
  const finalAssistantSignature = finalAssistantSegments.map(getTutorTurnKey).join("|");
  const finalAssistantSegmentsRef = useRef(finalAssistantSegments);
  finalAssistantSegmentsRef.current = finalAssistantSegments;

  const updateTranslation = useCallback(
    (cacheKey: string, state: TutorTurnTranslationState | null) => {
      setTranslations((current) => {
        const next = { ...current };
        if (state) {
          next[cacheKey] = state;
        } else {
          delete next[cacheKey];
        }
        translationsRef.current = next;
        return next;
      });
    },
    []
  );

  const abortPendingRequests = useCallback(() => {
    for (const controller of abortControllersRef.current.values()) {
      controller.abort();
    }
    abortControllersRef.current.clear();

    setTranslations((current) => {
      const next = Object.fromEntries(
        Object.entries(current).filter(([, state]) => state.status !== "loading")
      );
      translationsRef.current = next;
      return next;
    });
  }, []);

  const requestTranslation = useCallback(
    async (segment: TutorTranscriptSegment, force = false) => {
      const cacheKey = getTutorTurnTranslationCacheKey(segment, translationLanguageCode);
      const existing = translationsRef.current[cacheKey];
      if (!force && (existing?.status === "loading" || existing?.status === "success")) return;

      abortControllersRef.current.get(cacheKey)?.abort();
      const controller = new AbortController();
      abortControllersRef.current.set(cacheKey, controller);
      updateTranslation(cacheKey, {
        status: "loading",
        translationLanguage,
      });

      try {
        const response = await fetch("/api/translations/tutor-turn", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sourceText: String(segment.text || "").trim(),
            sourceLanguageCode,
            translationLanguageCode,
          }),
          signal: controller.signal,
        });
        const data = (await response.json().catch(() => ({}))) as {
          translation?: unknown;
          error?: unknown;
        };

        if (!response.ok || typeof data.translation !== "string" || !data.translation.trim()) {
          throw new Error(
            typeof data.error === "string" ? data.error : "Translation is unavailable"
          );
        }

        if (abortControllersRef.current.get(cacheKey) !== controller) return;
        updateTranslation(cacheKey, {
          status: "success",
          translation: data.translation.trim(),
          translationLanguage,
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        logger.warn("Tutor-turn translation request failed", {
          sourceLanguageCode,
          translationLanguageCode,
          error: error instanceof Error ? error.message : String(error),
        });
        updateTranslation(cacheKey, {
          status: "error",
          translationLanguage,
          error: "Translation unavailable",
        });
      } finally {
        if (abortControllersRef.current.get(cacheKey) === controller) {
          abortControllersRef.current.delete(cacheKey);
        }
      }
    },
    [sourceLanguageCode, translationLanguage, translationLanguageCode, updateTranslation]
  );

  useEffect(() => {
    const currentFinalAssistantSegments = finalAssistantSegmentsRef.current;
    const observed =
      observedByLanguageRef.current.get(translationLanguageCode) ?? new Set<string>();
    observedByLanguageRef.current.set(translationLanguageCode, observed);

    if (!enabled) {
      currentFinalAssistantSegments.forEach((segment) => observed.add(getTutorTurnKey(segment)));
      abortPendingRequests();
      previousConfigRef.current = { enabled: false, translationLanguageCode };
      return;
    }

    const previousConfig = previousConfigRef.current;
    const justEnabled = !previousConfig.enabled;
    const languageChanged =
      previousConfig.enabled && previousConfig.translationLanguageCode !== translationLanguageCode;

    if (justEnabled || languageChanged) {
      if (languageChanged) abortPendingRequests();
      currentFinalAssistantSegments.forEach((segment) => observed.add(getTutorTurnKey(segment)));
      const latestSegment = currentFinalAssistantSegments[currentFinalAssistantSegments.length - 1];
      if (latestSegment) void requestTranslation(latestSegment);
    } else {
      for (const segment of currentFinalAssistantSegments) {
        const turnKey = getTutorTurnKey(segment);
        if (observed.has(turnKey)) continue;
        observed.add(turnKey);
        void requestTranslation(segment);
      }
    }

    previousConfigRef.current = { enabled: true, translationLanguageCode };
  }, [
    abortPendingRequests,
    enabled,
    finalAssistantSignature,
    requestTranslation,
    translationLanguageCode,
  ]);

  useEffect(
    () => () => {
      for (const controller of abortControllersRef.current.values()) {
        controller.abort();
      }
      abortControllersRef.current.clear();
    },
    []
  );

  const getTranslation = useCallback(
    (segment: TutorTranscriptSegment) =>
      translations[getTutorTurnTranslationCacheKey(segment, translationLanguageCode)],
    [translationLanguageCode, translations]
  );

  const retryTranslation = useCallback(
    (segment: TutorTranscriptSegment) => {
      if (!enabled) return;
      void requestTranslation(segment, true);
    },
    [enabled, requestTranslation]
  );

  return {
    getTranslation,
    retryTranslation,
  };
}
